import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { AppLink } from '../components/Nav';
import { CardIcon, UsersIcon } from '../components/icons';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
} from '../components/ui';
import { ApiError, errorMessage } from '../lib/api';
import { countLabel, shortDate } from '../lib/format';
import type { OrgSummary, SubscriptionStatus } from '../lib/types';
import { atLeast, billingPortal, subscriptionNotice } from '../queries/orgs';

/**
 * Billing, owner-only. Everything real happens in Stripe — this page's job is
 * to say plainly where the subscription stands and hand off to the portal.
 */

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
};

const STATUS_TONE: Record<SubscriptionStatus, 'info' | 'go' | 'caution' | 'danger'> = {
  trialing: 'info',
  active: 'go',
  past_due: 'danger',
  canceled: 'caution',
};

/** Read once, on mount: Stripe Checkout sends people back here with ?checkout=. */
function readCheckoutOutcome(): 'success' | 'canceled' | null {
  const value = new URLSearchParams(window.location.search).get('checkout');
  if (value === 'success' || value === 'canceled') return value;
  return null;
}

function Billing({ org }: { org: OrgSummary }) {
  const [outcome] = useState<'success' | 'canceled' | null>(readCheckoutOutcome);
  const notice = subscriptionNotice(org.subscriptionStatus, org.trialEndsAt);

  const portal = useMutation({
    mutationFn: () => billingPortal(org.id),
    onSuccess: (response) => {
      window.location.assign(response.url);
    },
  });

  const notConfigured =
    portal.error instanceof ApiError &&
    (portal.error.status === 400 || portal.error.status === 501);

  return (
    <>
      <PageHeader
        title="Billing"
        subtitle={`${org.name} — subscription, seats, and payment method.`}
        actions={
          <AppLink to="/org/$slug" params={{ slug: org.slug }}>
            <Button>Back to workspace</Button>
          </AppLink>
        }
      />

      {outcome === 'success' ? (
        <div className="mb-5">
          <Banner tone="go" title="Payment method saved">
            Stripe confirmed the checkout. If the status below still says trial, give it a few
            seconds and reload — the webhook lands a moment after you do.
          </Banner>
        </div>
      ) : null}

      {outcome === 'canceled' ? (
        <div className="mb-5">
          <Banner tone="caution" title="Checkout canceled">
            Nothing was charged and nothing changed. The workspace keeps working exactly as it did;
            you can start checkout again whenever you like.
          </Banner>
        </div>
      ) : null}

      {notice ? (
        <div className="mb-5">
          <Banner tone={notice.tone} title={notice.title}>
            {notice.body}
          </Banner>
        </div>
      ) : null}

      <section className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-h4 text-chalk">Subscription</h2>
            <p className="mt-1 text-caption text-chalk-dim">
              Per active member, billed monthly through Stripe.
            </p>
          </div>
          <Badge tone={STATUS_TONE[org.subscriptionStatus]}>
            {STATUS_LABEL[org.subscriptionStatus]}
          </Badge>
        </div>

        <dl className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-caption text-chalk-faint">Seats</dt>
            <dd className="mt-0.5 text-h4 text-chalk tnum">
              {countLabel(org.seatCount, 'seat')}
            </dd>
            <p className="mt-0.5 text-caption text-chalk-faint">
              One per active member. Removing someone frees theirs.
            </p>
          </div>
          <div>
            <dt className="text-caption text-chalk-faint">
              {org.subscriptionStatus === 'trialing' ? 'Trial ends' : 'Trial ended'}
            </dt>
            <dd className="mt-0.5 text-h4 text-chalk tnum">
              {org.trialEndsAt ? shortDate(org.trialEndsAt) : '—'}
            </dd>
            <p className="mt-0.5 text-caption text-chalk-faint">
              {org.trialEndsAt
                ? 'Recordings already made stay watchable either way.'
                : 'This workspace never had a trial window recorded.'}
            </p>
          </div>
        </dl>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            busy={portal.isPending}
            onClick={() => portal.mutate()}
          >
            <CardIcon size={15} />
            {org.subscriptionStatus === 'trialing' ? 'Set up billing' : 'Manage billing'}
          </Button>
          <span className="text-caption text-chalk-faint">
            Opens Stripe, where cards, invoices and cancellation live.
          </span>
        </div>

        {portal.isError ? (
          <div className="mt-4">
            {notConfigured ? (
              <Banner tone="caution" title={errorMessage(portal.error)}>
                Stripe is not wired up on this deployment, so there is no portal to open. Teams
                still work — the trial and the seat count are tracked by the API either way. Set
                the Stripe keys on slate-api and this button starts working.
              </Banner>
            ) : (
              <ErrorState
                title="Could not open the billing portal"
                message={errorMessage(portal.error)}
                onRetry={() => portal.mutate()}
              />
            )}
          </div>
        ) : null}
      </section>

      <p className="mt-5 text-caption text-chalk-faint">
        Seats follow the member list.{' '}
        <AppLink
          to="/org/$slug/members"
          params={{ slug: org.slug }}
          className="text-signal hover:underline"
        >
          Manage members
        </AppLink>
      </p>
    </>
  );
}

export function OrgBillingPage({ slug }: { slug: string }) {
  const { orgs, loading, error, refetch } = useWorkspace();

  if (loading) return <Loading label="Loading your workspaces" />;

  if (error) {
    return (
      <ErrorState
        title="Could not load your workspaces"
        message={errorMessage(error)}
        onRetry={refetch}
      />
    );
  }

  const org = orgs.find((candidate) => candidate.slug === slug);

  if (!org) {
    return (
      <>
        <PageHeader title="Billing" />
        <EmptyState
          title="You are not a member of this workspace"
          icon={<UsersIcon size={26} />}
          action={
            <AppLink to="/">
              <Button variant="primary">Go to your library</Button>
            </AppLink>
          }
        >
          <p>
            The address may be wrong, or your membership may have been removed. Ask a teammate to
            send you a fresh invitation.
          </p>
        </EmptyState>
      </>
    );
  }

  if (!atLeast(org.role, 'owner')) {
    return (
      <>
        <PageHeader title="Billing" subtitle={org.name} />
        <Banner
          tone="info"
          title="Only owners see billing"
          action={
            <AppLink to="/org/$slug" params={{ slug: org.slug }}>
              <Button size="sm">Back to workspace</Button>
            </AppLink>
          }
        >
          Payment details, invoices and the subscription belong to the workspace owner. If something
          needs paying, they are the person to nudge.
        </Banner>
      </>
    );
  }

  return <Billing key={org.id} org={org} />;
}
